/**
 * One permission level, as a pill — `organization-build-book.md` §6.4.
 *
 * The person card's readout draws eighteen of these at once, so the level has
 * to be readable at a glance rather than read: none is faint, view is indigo,
 * edit is teal, full is green, and the ladder runs cool to warm the way the
 * four levels do. `LEVEL_TONES` in `shared` names the four tones, so the
 * API's idea of a level and the screen's cannot drift.
 *
 * The colours are applied as inline custom properties, the way `StatusChip`
 * does it, rather than as status utility classes: Tailwind's config does not
 * define any (the token guard in `tests/design-tokens.test.ts` says so), and
 * a class the config does not define is a class that renders as nothing.
 */
import { LEVEL_TONES, type PermissionLevel } from '@rowted/shared'
import { cn } from '../../lib/cn.js'

type Tone = (typeof LEVEL_TONES)[PermissionLevel]

/** `[foreground, background]` token names, per tone. */
const TONE_VARS: Record<Tone, [string, string]> = {
  faint: ['--color-text-faint', '--color-line-soft'],
  indigo: ['--status-job-reviewed-fg', '--status-job-reviewed-bg'],
  teal: ['--status-job-approved-fg', '--status-job-approved-bg'],
  green: ['--color-accent-dark', '--color-accent-soft'],
}

const colours = (level: PermissionLevel) => {
  const [fg, bg] = TONE_VARS[LEVEL_TONES[level]]
  return { color: `rgb(var(${fg}))`, background: `rgb(var(${bg}))` }
}

export function LevelPill({ level }: { level: PermissionLevel }) {
  return (
    <span
      style={colours(level)}
      className="inline-flex h-6 items-center rounded-chip px-2 text-[11.5px] font-bold capitalize"
    >
      {level}
    </span>
  )
}

/**
 * The four levels as one control — §6.6's "four-way level control".
 *
 * A dropdown would hide three quarters of an ordered scale behind a click.
 * Seeing where a role sits on that scale, and how far it is from the one
 * above, is most of what the screen is for.
 */
export function LevelPicker({ value, onChange, label, disabled }: {
  value: PermissionLevel
  onChange: (next: PermissionLevel) => void
  label: string
  disabled?: boolean
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn('flex w-[260px] shrink-0 gap-0.5 rounded-[9px] border border-line',
        'bg-segmented-track p-0.5')}
    >
      {(['none', 'view', 'edit', 'full'] as const).map((level) => (
        <button
          key={level}
          type="button"
          role="radio"
          aria-checked={value === level}
          disabled={disabled}
          onClick={() => onChange(level)}
          style={value === level ? colours(level) : undefined}
          className={cn('h-7 flex-1 rounded-[7px] text-[12px] font-semibold capitalize',
            'disabled:cursor-not-allowed',
            value !== level && 'text-text-faint hover:text-text-muted')}
        >
          {level}
        </button>
      ))}
    </div>
  )
}
